import { buildCsvFromRows } from '../../../shared/csv-core/infra/buildCsv';
import { listBrandsLocal } from '../brandsRepo';
import { listCategories } from '../categoriesRepo';
import { listCatalogProducts } from '../catalogReadRepo';
import { listSuppliersLocal } from '../suppliersRepo';
import { listVariantsForProduct } from '../productVariantsRepo';
import type { CatalogCsvEntityKind } from '../../domain/csv/csvEntityKinds';
import { catalogCsvHeadersFor } from './catalogCsvSchemas';
import { catalogCsvTemplateExampleRows } from './catalogCsvTemplateRows';
import { formatCsvAppliesTo, formatCsvItemType } from '../../domain/csv/csvParseHelpers';
import { getSessionOrganizationId } from '../../../shell/session/sessionOrganization';
import { resolveCategoryDisplayName } from '../categoriesRepo';

function boolCell(active: boolean): string {
  return active ? 'si' : 'no';
}

export async function buildCatalogCsvExport(entity: CatalogCsvEntityKind): Promise<string> {
  const headers = [...catalogCsvHeadersFor(entity)];

  switch (entity) {
    case 'categories': {
      const categories = await listCategories({ includeInactive: true });
      const rows = categories.map((c) => [
        c.id,
        c.name,
        c.code ?? '',
        formatCsvAppliesTo(c.appliesTo),
        c.defaultMarginPercent != null ? String(c.defaultMarginPercent) : '',
        c.description ?? '',
        boolCell(c.isActive),
      ]);
      return buildCsvFromRows([...catalogCsvHeadersFor('categories')], rows);
    }
    case 'brands': {
      const brands = await listBrandsLocal();
      const rows = brands.map((b) => [
        b.id,
        b.name,
        b.code ?? '',
        b.website ?? '',
        b.defaultMarkupPercent != null ? String(b.defaultMarkupPercent) : '',
        boolCell(b.isActive),
      ]);
      return buildCsvFromRows([...catalogCsvHeadersFor('brands')], rows);
    }
    case 'suppliers': {
      const suppliers = await listSuppliersLocal();
      const rows = suppliers.map((s) => [
        s.id,
        s.name,
        s.code ?? '',
        s.taxId ?? '',
        s.contactName ?? '',
        s.contactEmail ?? '',
        s.contactPhone ?? '',
        s.address ?? '',
        s.city ?? '',
        s.state ?? '',
        s.zip ?? '',
        s.paymentTerms ?? '',
        s.leadTimeDays != null ? String(s.leadTimeDays) : '',
        s.minimumOrderAmount != null ? String(s.minimumOrderAmount) : '',
        s.website ?? '',
        s.notes ?? '',
        s.accountNumber ?? '',
        boolCell(s.isActive),
      ]);
      return buildCsvFromRows([...catalogCsvHeadersFor('suppliers')], rows);
    }
    case 'products': {
      const orgId = await getSessionOrganizationId();
      if (!orgId) return buildCsvFromRows([...catalogCsvHeadersFor('products')], []);
      const products = await listCatalogProducts({
        status: 'all',
        itemType: 'all',
        limit: 50_000,
        offset: 0,
      });
      const brands = await listBrandsLocal();
      const suppliers = await listSuppliersLocal();
      const brandName = new Map(brands.map((b) => [b.id, b.name]));
      const supplierName = new Map(suppliers.map((s) => [s.id, s.name]));

      const rows: string[][] = [];
      for (const p of products) {
        const categoryLabel = await resolveCategoryDisplayName(p.categoryId);
        const brand = p.brandId != null ? (brandName.get(p.brandId) ?? '') : '';
        const supplier = p.preferredSupplierId != null ? (supplierName.get(p.preferredSupplierId) ?? '') : '';

        // Fila del parent (sin columnas de variante).
        rows.push([
          p.sku ?? '',
          p.name,
          String(p.price),
          p.barcode ?? '',
          formatCsvItemType(p.itemType),
          p.categoryId ?? '',
          categoryLabel === '—' ? '' : categoryLabel,
          p.brandId ?? '',
          brand,
          p.preferredSupplierId ?? '',
          supplier,
          p.supplierItemCode ?? '',
          p.internalNotes ?? '',
          boolCell(p.isActive !== false),
          // Columnas de variante vacías para el parent.
          '', '', '', '', '', '', '', '', '', '',
        ]);

        // #712: Una fila por cada variante activa.
        const variants = await listVariantsForProduct(p.id);
        for (const v of variants) {
          const attrs = v.attributes ?? {};
          const attrEntries = Object.entries(attrs).slice(0, 3);
          const axisCells: string[] = [];
          for (let i = 0; i < 3; i += 1) {
            if (i < attrEntries.length) {
              axisCells.push(attrEntries[i][0], attrEntries[i][1]);
            } else {
              axisCells.push('', '');
            }
          }
          rows.push([
            p.sku ?? '',           // parent sku (repetido)
            '',                    // nombre (vacío — hereda del parent)
            '',                    // precio (vacío — hereda del parent)
            '',                    // codigo_barras (vacío — hereda del parent)
            '',                    // tipo (vacío — hereda del parent)
            '',                    // categoria_id
            '',                    // categoria
            '',                    // marca_id
            '',                    // marca
            '',                    // proveedor_preferido_id
            '',                    // proveedor_preferido
            '',                    // codigo_proveedor
            '',                    // notas
            '',                    // activo (vacío — hereda del parent)
            v.sku ?? '',
            v.barcode ?? '',
            v.price != null ? String(v.price) : '',
            axisCells[0], axisCells[1],
            axisCells[2], axisCells[3],
            axisCells[4], axisCells[5],
          ]);
        }
      }
      return buildCsvFromRows(headers, rows);
    }
    default:
      return buildCsvFromRows(headers, []);
  }
}

export function buildCatalogCsvTemplate(entity: CatalogCsvEntityKind): string {
  const headers = [...catalogCsvHeadersFor(entity)];
  return buildCsvFromRows(headers, catalogCsvTemplateExampleRows(entity));
}
